import React, { useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { OrderImport } from './order-import';
import messages from './order-import-modal/messages';
import { refreshStatusNames } from '../../enums/store-refresh-status';
import { LogService } from '../../services/log-service';
import GlobalSelector from '../../store/global-selectors';
import * as Actions from '../../store/orders/import/orders.import.actions';
import { setStoreGuid } from '../../store/orders/import/orders.import.actions';
import { getSavedMappingsPromise } from '../../store/orders/import/orders.import.api';
import { getStoresPromise } from '../../store/seller/seller.api';
import { ImportType } from '../csv-import/data/types/import-type';
import { useCsvImport } from '../csv-import/ui/hooks/use-csv-import';

const noop = () => {
  // Default no-op function for context
};

const OrderCSVImportContext = React.createContext({
  isLoading: false,
  openCSVImport: noop,
  closeCSVImport: noop,
  disabledStoreGuidSet: new Set(),
  storeDropdownOptions: [],
});
export const useOrderCSVImportContext = () => useContext(OrderCSVImportContext);

const getDisabledStoreGuidSet = (stores = []) => {
  const disabledStoreGuids = stores
    .filter((store) => store.refreshStatus === refreshStatusNames.queued || store.refreshStatus === refreshStatusNames.updating)
    .map((store) => store.storeGuid);
  return new Set(disabledStoreGuids);
};

export const OrderCSVImportContextProvider = ({ children }) => {
  const { open, isOpen, close } = useCsvImport(ImportType.order);
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeStores = useSelector(GlobalSelector.selectSellerActiveManualStores);
  const [disabledStoreGuidSet, setDisabledStoreGuidSet] = useAsyncSafeState(() => getDisabledStoreGuidSet(activeStores));
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const storeDropdownOptions = useMemo(
    () =>
      activeStores
        .sort((storeA, storeB) => storeA.name.localeCompare(storeB.name))
        .reduce((acc, store) => {
          if (disabledStoreGuidSet.has(store.storeGuid)) {
            acc.push({
              ...store,
              name: intl.formatMessage(messages.importInProgress, {
                storeName: store.name,
              }),
              disabled: true,
            });
          } else {
            acc.push(store);
          }
          return acc;
        }, []),
    [activeStores, disabledStoreGuidSet, intl],
  );

  const openCSVImport = useCallback(async () => {
    if (isOpen) {
      return;
    }
    if (activeStores.length === 1) {
      dispatch(setStoreGuid(activeStores[0].storeGuid));
    }
    setIsLoading(true);
    open();
    try {
      const stores = await getStoresPromise();
      const unavailableStoreGuids = getDisabledStoreGuidSet(stores);
      setDisabledStoreGuidSet(unavailableStoreGuids);
    } catch (e) {
      LogService.logWarning('Order CSV Import - Failed to load store statuses', e);
    }

    try {
      const mappings = await getSavedMappingsPromise();
      Actions.getMappingSuccess(mappings);
    } catch (e) {
      Actions.getMappingFailure(e);
    }

    setIsLoading(false);
  }, [isOpen, activeStores, setIsLoading, open, dispatch, setDisabledStoreGuidSet]);

  const closeCSVImport = useCallback(() => {
    close();
  }, [close]);

  const value = useMemo(
    () => ({
      isLoading,
      openCSVImport,
      closeCSVImport,
      disabledStoreGuidSet,
      storeDropdownOptions,
    }),
    [isLoading, openCSVImport, closeCSVImport, disabledStoreGuidSet, storeDropdownOptions],
  );
  return (
    <OrderCSVImportContext.Provider value={value}>
      {children}
      {isOpen ? <OrderImport /> : null}
    </OrderCSVImportContext.Provider>
  );
};

export default OrderCSVImportContextProvider;
